/**
 * The SDK-less Descope session layer (ADR 008), shared by both SPAs.
 *
 * `apps/web` and `apps/admin` authenticate against different Descope projects
 * — a tenant project and a staff project (ADR 009) — but hold the session the
 * same way: both JWTs in `localStorage`, refreshed over Descope's REST API.
 * Every function that talks to Descope takes the project id, so nothing here
 * knows which app it is running in. The two apps live on different origins, so
 * the shared `DS` / `DSR` keys never meet.
 */
export const SESSION_TOKEN_KEY = "DS";
const REFRESH_TOKEN_KEY = "DSR";
export const SESSION_EXPIRED_EVENT = "headerhawk:session-expired";
export function getSessionToken() {
    return localStorage.getItem(SESSION_TOKEN_KEY);
}
export function getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
}
function decodeJwtPayload(token) {
    try {
        const parts = token.split(".");
        const encoded = parts[1];
        if (parts.length !== 3 || !encoded)
            return null;
        const payload = atob(encoded.replace(/-/g, "+").replace(/_/g, "/"));
        return JSON.parse(payload);
    }
    catch {
        return null;
    }
}
export function isTokenExpired(token) {
    const payload = decodeJwtPayload(token);
    const exp = payload?.["exp"];
    if (typeof exp !== "number")
        return true;
    return Date.now() >= exp * 1000;
}
export function getTokenClaims(token) {
    const payload = decodeJwtPayload(token);
    if (!payload)
        return null;
    return {
        sub: payload["sub"],
        email: payload["email"],
        name: payload["name"],
        exp: payload["exp"],
    };
}
let refreshPromise = null;
export async function refreshSession(projectId) {
    if (refreshPromise)
        return refreshPromise;
    refreshPromise = doRefresh(projectId).finally(() => {
        refreshPromise = null;
    });
    return refreshPromise;
}
async function doRefresh(projectId) {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
        notifySessionExpired();
        return { ok: false, expired: true };
    }
    try {
        const response = await fetch("https://api.descope.com/v1/auth/try-refresh?dcs=t&dcr=t", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${projectId}:${refreshToken}`,
                "x-descope-project-id": projectId,
            },
            body: "{}",
        });
        if (!response.ok) {
            // Only a *rejected* refresh token ends the session. A 429 or a Descope
            // 5xx means "ask again later" - clearing tokens there would sign a user
            // out mid-task over a transient outage, with nothing left to recover
            // from. Network-level throws take the same path via the catch below.
            if (response.status === 401 || response.status === 403) {
                clearTokens();
                notifySessionExpired();
                return { ok: false, expired: true };
            }
            return { ok: false };
        }
        const data = (await response.json());
        if (data.sessionJwt) {
            localStorage.setItem(SESSION_TOKEN_KEY, data.sessionJwt);
        }
        if (data.refreshJwt) {
            localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshJwt);
        }
        return { ok: true, sessionJwt: data.sessionJwt };
    }
    catch {
        return { ok: false };
    }
}
/**
 * Fetch user info from Descope's /v1/auth/me endpoint.
 * This replaces the Descope SDK's useUser() hook with a
 * lightweight HTTP call that doesn't require loading the SDK.
 *
 * It is also the only place a staff session's email comes from: staff session
 * tokens carry no `email` claim (task 247), but this endpoint answers a
 * two-factor staff refresh token with the user's email like any other.
 */
export async function fetchUserInfo(projectId) {
    const refreshToken = getRefreshToken();
    if (!refreshToken)
        return null;
    try {
        const response = await fetch("https://api.descope.com/v1/auth/me", {
            method: "GET",
            headers: {
                Authorization: `Bearer ${projectId}:${refreshToken}`,
            },
        });
        if (!response.ok)
            return null;
        const data = (await response.json());
        return {
            userId: data.userId ?? "",
            email: data.email,
            name: data.name,
            picture: data.picture,
        };
    }
    catch {
        return null;
    }
}
export function clearTokens() {
    localStorage.removeItem(SESSION_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
}
export async function logoutSession(projectId) {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
        try {
            await fetch("https://api.descope.com/v1/auth/logout", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${projectId}:${refreshToken}`,
                },
                body: "{}",
            });
        }
        catch {
            // Best effort - clear local tokens regardless
        }
    }
    clearTokens();
}
/**
 * Announce that the session is over and cannot be refreshed.
 *
 * Only the sites and provisioning queries used to react to a 401, so when a
 * refresh token finally expired every other screen dead-ended on "Failed to
 * load" with no way out. The auth provider listens for this and flips
 * `isAuthenticated`, which lets the app's auth gate redirect to login from any
 * route.
 */
export function notifySessionExpired() {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}
/**
 * Notify the SessionAuthProvider that tokens changed in the same tab.
 * The `storage` event only fires in other tabs, so this custom event
 * handles same-tab updates (e.g. after login/signup Descope flows).
 */
export function notifySessionChange() {
    window.dispatchEvent(new CustomEvent("headerhawk:session-change"));
}
//# sourceMappingURL=token-manager.js.map